import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Position, PieceType, validateMove, getGameStatus } from '@/lib/chess-engine';
import { gameStateToFen, fenToGameState } from '@/lib/fen-parser';
import { normalizeGameState } from '@/lib/gameStateUtils';
import { useNavigate } from 'react-router-dom';
import { useRematchSystem } from '@/hooks/useRematchSystem';

export interface EloUpdateResult {
  white_player: {
    old_elo: number;
    new_elo: number;
    elo_change: number;
    coins_awarded: number;
    gems_awarded: number;
  };
  black_player: {
    old_elo: number;
    new_elo: number;
    elo_change: number;
    coins_awarded: number;
    gems_awarded: number;
  };
}

export interface GameSession {
  id: string;
  white_player_id: string;
  black_player_id: string;
  game_state: any;
  current_turn: 'white' | 'black';
  game_status: string;
  time_control: string;
  white_time_ms: number;
  black_time_ms: number;
  increment_ms: number;
  current_fen: string;
  move_count: number;
  last_move: any;
  last_tick_at?: string;
  clocks_paused?: boolean;
  winner?: string | null;
  draw_offer_from?: string | null;
  rematch_offer_from?: string | null;
  rematch_accepted?: boolean;
  rematch_session_id?: string | null;
}

export function useDirectGameSession(gameSessionId: string | null) {
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [playerColor, setPlayerColor] = useState<'white' | 'black' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [eloUpdateResult, setEloUpdateResult] = useState<EloUpdateResult | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Use unified rematch system
  const { rematchState, offerRematch, acceptRematch, declineRematch } = 
    useRematchSystem(gameSessionId);

  // Navigate to new game when rematch is created
  useEffect(() => {
    if (rematchState.rematchSessionId) {
      navigate(`/game/${rematchState.rematchSessionId}`);
    }
  }, [rematchState.rematchSessionId, navigate]);

  // Update ELO ratings after game completion
  const updateEloRatings = useCallback(async (sessionId: string) => {
    try {
      console.log('🎯 Updating ELO ratings for game:', sessionId);
      const { data, error } = await supabase.rpc('update_player_elos_secure', {
        _game_session_id: sessionId
      });

      if (error) throw error;
      
      console.log('✅ ELO update result:', data);
      setEloUpdateResult(data as unknown as EloUpdateResult);
      return data;
    } catch (error: any) {
      console.error('❌ Error updating ELO:', error);
      toast({
        title: "ELO Update Failed",
        description: error.message || "Could not update ratings",
        variant: "destructive"
      });
      return null;
    }
  }, [toast]);

  // Fetch game session
  const fetchGameSession = useCallback(async () => {
    if (!gameSessionId || !user) return;

    try {
      setError(null);
      const { data, error } = await supabase
        .from('game_sessions')
        .select('*')
        .eq('id', gameSessionId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          setError('Game session not found. It may have been deleted or never existed.');
        } else {
          setError(`Database error: ${error.message}`);
        }
        setIsLoading(false);
        return;
      }

      if (!data) {
        setError('Game session not found');
        setIsLoading(false);
        return;
      }

      // Success
      setGameSession(data as GameSession);
      setPlayerColor(data.white_player_id === user.id ? 'white' : 'black');
      setIsLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load game');
      setIsLoading(false);
    }
  }, [gameSessionId, user]);

  // Add timeout for loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading && !gameSession) {
        setLoadTimeout(true);
        setError('Game session not found or connection timeout');
        setIsLoading(false);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [isLoading, gameSession]);

  // Retry load function
  const retryLoad = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setLoadTimeout(false);
    fetchGameSession();
  }, [fetchGameSession]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!gameSessionId || !user) return;

    fetchGameSession();

    const channel = supabase
      .channel(`game-session-${gameSessionId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'game_sessions',
        filter: `id=eq.${gameSessionId}`
      }, (payload) => {
        console.log('[DirectGameSession] Real-time update:', payload);
        setGameSession(payload.new as GameSession);
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [gameSessionId, user, fetchGameSession]);

  // Make a move with full chess validation
  const makeMove = useCallback(async (from: Position, to: Position, promotion?: PieceType) => {
    if (!gameSessionId || !gameSession || !user) return false;

    // Verify it's the player's turn
    if ((gameSession.current_turn === 'white' && gameSession.white_player_id !== user.id) ||
        (gameSession.current_turn === 'black' && gameSession.black_player_id !== user.id)) {
      toast({
        title: "Not Your Turn",
        description: "Wait for your opponent to make their move",
        variant: "destructive"
      });
      return false;
    }

    try {
      // Normalize and validate the move
      const currentGameState = normalizeGameState(gameSession.game_state);
      const moveResult = validateMove(currentGameState, from, to, promotion);

      if (!moveResult.isLegal || !moveResult.newState) {
        toast({
          title: "Illegal Move",
          description: moveResult.reason || "This move is not legal",
          variant: "destructive"
        });
        return false;
      }

      // Convert new state to FEN
      const newFen = gameStateToFen(moveResult.newState);
      const nextTurn = gameSession.current_turn === 'white' ? 'black' : 'white';

      // Check if move causes game to end (checkmate or stalemate)
      // We check the status AFTER the move has been applied
      const newGameState = moveResult.newState;
      const newStatus = getGameStatus(newGameState);
      const isCheckmate = newStatus.status === 'checkmate';
      const isStalemate = newStatus.status === 'stalemate';
      
      let updateData: any = {
        game_state: moveResult.newState as any,
        current_fen: newFen,
        current_turn: nextTurn,
        move_count: gameSession.move_count + 1,
        last_move: { from, to, promotion },
        last_tick_at: new Date().toISOString(),
        white_time_ms: gameSession.current_turn === 'white' 
          ? gameSession.white_time_ms + gameSession.increment_ms 
          : gameSession.white_time_ms,
        black_time_ms: gameSession.current_turn === 'black' 
          ? gameSession.black_time_ms + gameSession.increment_ms 
          : gameSession.black_time_ms,
        updated_at: new Date().toISOString()
      };

      // If game ends, update status and winner
      if (isCheckmate || isStalemate) {
        updateData.game_status = isCheckmate ? 'checkmate' : 'draw';
        updateData.ended_at = new Date().toISOString();
        
        if (isCheckmate) {
          // Winner is the player who just moved
          updateData.winner = gameSession.current_turn;
        } else {
          updateData.winner = null; // Draw
        }
      }

      // Update database (convert GameState to JSON-compatible format)
      const { error } = await supabase
        .from('game_sessions')
        .update(updateData)
        .eq('id', gameSessionId);

      if (error) throw error;

      // Update ELO if game ended
      if (isCheckmate || isStalemate) {
        await updateEloRatings(gameSessionId);
      }

      return true;
    } catch (error: any) {
      console.error('Error making move:', error);
      toast({
        title: "Move Failed",
        description: error.message || "Could not make move",
        variant: "destructive"
      });
      return false;
    }
  }, [gameSessionId, gameSession, user, toast]);

  // Resign the game
  const resign = useCallback(async () => {
    if (!gameSessionId || !gameSession || !user) return false;

    try {
      const winnerColor = gameSession.white_player_id === user.id ? 'black' : 'white';

      const { error } = await supabase
        .from('game_sessions')
        .update({
          game_status: 'resignation',
          winner: winnerColor,
          ended_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', gameSessionId);

      if (error) throw error;

      // Update ELO after resignation
      await updateEloRatings(gameSessionId);

      toast({
        title: "Game Resigned",
        description: "You have resigned from the game",
      });

      return true;
    } catch (error: any) {
      console.error('Error resigning:', error);
      toast({
        title: "Resign Failed",
        description: error.message || "Could not resign",
        variant: "destructive"
      });
      return false;
    }
  }, [gameSessionId, gameSession, user, toast]);

  // Offer a draw
  const offerDraw = useCallback(async () => {
    if (!gameSessionId || !gameSession || !user) return false;

    try {
      const { error } = await supabase
        .from('game_sessions')
        .update({
          draw_offer_from: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameSessionId);

      if (error) throw error;

      toast({
        title: "Draw Offered",
        description: "Waiting for opponent's response",
      });

      return true;
    } catch (error: any) {
      console.error('Error offering draw:', error);
      toast({
        title: "Draw Offer Failed",
        description: error.message || "Could not offer draw",
        variant: "destructive"
      });
      return false;
    }
  }, [gameSessionId, gameSession, user, toast]);

  // Accept a draw offer
  const acceptDraw = useCallback(async () => {
    if (!gameSessionId || !gameSession) return false;

    try {
      const { error } = await supabase
        .from('game_sessions')
        .update({
          game_status: 'draw',
          draw_offer_from: null,
          winner: null,
          ended_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', gameSessionId);

      if (error) throw error;

      // Update ELO after draw
      await updateEloRatings(gameSessionId);

      toast({
        title: "Draw Accepted",
        description: "Game ended in a draw",
      });

      return true;
    } catch (error: any) {
      console.error('Error accepting draw:', error);
      toast({
        title: "Accept Draw Failed",
        description: error.message || "Could not accept draw",
        variant: "destructive"
      });
      return false;
    }
  }, [gameSessionId, gameSession, toast]);

  // Decline a draw offer
  const declineDraw = useCallback(async () => {
    if (!gameSessionId) return false;

    try {
      const { error } = await supabase
        .from('game_sessions')
        .update({
          draw_offer_from: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', gameSessionId);

      if (error) throw error;

      toast({
        title: "Draw Declined",
        description: "Continue playing",
      });

      return true;
    } catch (error: any) {
      console.error('Error declining draw:', error);
      toast({
        title: "Decline Draw Failed",
        description: error.message || "Could not decline draw",
        variant: "destructive"
      });
      return false;
    }
  }, [gameSessionId, toast]);

  return {
    gameSession,
    playerColor,
    isLoading,
    error,
    loadTimeout,
    retryLoad,
    eloUpdateResult,
    makeMove,
    resign,
    offerDraw,
    acceptDraw,
    declineDraw,
    rematchState,
    offerRematch,
    acceptRematch,
    declineRematch,
    refreshGame: fetchGameSession
  };
}
